import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { listUsers, createUser, deleteUser } from "../lib/api";
import { Toolbar, Modal, Field, inputCls, Card } from "./AdminLayout";
import { IconBtn } from "./ArticlesAdmin";
import { Trash2, Shield, PenLine } from "lucide-react";

const empty = { email: "", name: "", password: "", role: "reporter" };
const ROLE_LABELS = { admin: "Editor / Admin", reporter: "Reporter", contributor: "Community Voice", coach: "Coach" };

export default function StaffAdmin() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => listUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.password) return toast.error("All fields are required.");
    setBusy(true);
    try {
      await createUser(form);
      toast.success("Account created.");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create account."); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})?`)) return;
    try { await deleteUser(u.id); toast.success("Account removed."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove."); }
  };

  return (
    <div>
      <Toolbar title="Staff Accounts" subtitle="Create logins for reporters and editors" onAdd={() => { setForm(empty); setOpen(true); }} addLabel="Add Staff" />
      <Card className="overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3 text-right">Action</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`staff-${u.id}`}>
                <td className="px-4 py-3 font-semibold text-[var(--navy)]">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold ${u.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {u.role === "admin" ? <Shield size={11} /> : <PenLine size={11} />} {ROLE_LABELS[u.role] || "Reporter"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right"><IconBtn onClick={() => remove(u)} danger testid={`delete-staff-${u.id}`}><Trash2 size={14} /></IconBtn></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {open && (
        <Modal title="Add Staff Member" onClose={() => setOpen(false)} onSubmit={submit} busy={busy} submitLabel="Create Account">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name"><input data-testid="staff-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
            <Field label="Role"><select data-testid="staff-role" value={form.role} onChange={set("role")} className={inputCls}><option value="reporter">Reporter (articles & photos)</option><option value="contributor">Community Voice (submits for approval)</option><option value="coach">Coach (game reports for approval)</option><option value="admin">Editor / Admin (full access)</option></select></Field>
            <Field label="Email"><input data-testid="staff-email" type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
            <Field label="Temporary password"><input data-testid="staff-password" value={form.password} onChange={set("password")} className={inputCls} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
